import React, { useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import L from 'leaflet';
import 'leaflet.sync';
import _ from 'underscore';
import { makeStyles } from '@mui/styles';
import { Button, FormControlLabel, TextField, Card, Grid, Switch } from '@mui/material';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import {
	SetFormState, GetSiteIdLocation, ToggleCarriersColorsDialog
} from '../../actions/DualMapActionsGeneric.js';

const useStyles = makeStyles((theme) => ({
	root: {
		height: '100%',
		display: 'flex',
		flexDirection: 'column',
	},
	drawer: {
		width: 420,
		maxWidth: '100%',
	},
	header: {
		padding: theme.spacing(2, 1),
		display: 'flex',
		justifyContent: 'space-between',
	},
	buttonIcon: {
		marginRight: theme.spacing(1),
	},
	content: {
		padding: theme.spacing(0, 0),
		flexGrow: 1,
	},
	contentSection: {
		padding: theme.spacing(1, 0),
	},
	contentSectionHeader: {
		display: 'flex',
		justifyContent: 'space-between',
		cursor: 'pointer',
	},
	contentSectionContent: {},
	formGroup: {
		padding: theme.spacing(2, 0),
	},
	fieldGroup: {
		display: 'flex',
		alignItems: 'center',
	},
	field: {
		marginTop: 0,
		marginBottom: 0,
	},
	flexGrow: {
		flexGrow: 1,
	},
	addButton: {
		marginLeft: theme.spacing(1),
	},
	tags: {
		marginTop: theme.spacing(1),
	},
	minAmount: {
		marginRight: theme.spacing(3),
	},
	maxAmount: {
		marginLeft: theme.spacing(3),
	},
	radioGroup: {},
	actions: {
		padding: theme.spacing(3),
		'& > * + *': {
			marginTop: theme.spacing(2),
		},
	},
	button: {
		'& > *': {
			margin: theme.spacing(1),
		},
	}
}));

const CompareBox = (props) => {
	const { className, open, onClose, onFilter, filterDisabled, onClear, ...rest } = props;
	const classes = useStyles();
	const dispatch = useDispatch();
	const setFormState = formState => dispatch(SetFormState(formState));
	const formState = useSelector((state) => state.dualMapReducer.formState);
	const maps = useSelector(state => state.mapReducer.maps);

	// Map
	const mapOne = maps.filter(item => item.id === 'dual-map')[0];
	const driveTest = useSelector(state => state.dualMapReducer.driveTest);
	const failuresVoice = useSelector(state => state.dualMapReducer.failuresVoice);
	const failuresPacket = useSelector(state => state.dualMapReducer.failuresPacket);
	const techBandVoice = useSelector(state => state.dualMapReducer.techBandVoice);
	const techBandPacket = useSelector(state => state.dualMapReducer.techBandPacket);
	const sitesIds = useSelector(state => state.dualMapReducer.sitesIds);
	const siteIdLocation = useSelector(state => state.dualMapReducer.siteIdLocation);

	// Map Compare
	const mapTwo = maps.filter(item => item.id === 'dual-map-compare')[0];
	const driveTestCompare = useSelector(state => state.dualMapReducer.driveTestCompare);
	const failuresVoiceCompare = useSelector(state => state.dualMapReducer.failuresVoiceCompare);
	const failuresPacketCompare = useSelector(state => state.dualMapReducer.failuresPacketCompare);
	const techBandVoiceCompare = useSelector(state => state.dualMapReducer.techBandVoiceCompare);
	const techBandPacketCompare = useSelector(state => state.dualMapReducer.techBandPacketCompare);

	const makeDetailsSiteId = (item) => {
		const table = `<table class="table table-striped">
						<tr>
						<th>SiteId: </th>
						<td>${item.siteId}</td>
						</tr>
						<tr>
						<th>Latitude: </th>
						<td>${item.latitude}</td>
						</tr>
						<tr>
						<th>Longitude: </th>
						<td>${item.longitude}</td>
						</tr>
					</table>`;
		return table;
	};

	const _addSiteLocation = (map, data, layerName, flyTo) => {
		map.RemoveLayerById(layerName);
		const markers = [];

		const packetIcondrop = L.icon({
			iconUrl: 'images/leaflet/marker-icon.png',
			// iconSize: [35, 35], // size of the icon
			iconAnchor: [13, 40], // point of the icon which will correspond to marker's location
			popupAnchor: [0, -38] // point from which the popup should open relative to the iconAnchor
		});

		if (!_.isEmpty(data)) {
			const marker = L.marker([data.latitude, data.longitude], { title: data.failureId, icon: packetIcondrop });
			markers.push(marker.bindPopup(`<br>${makeDetailsSiteId(data)}<br>`, { autoPan: false }));

			if (flyTo) {
				map.mapContext.map.flyTo([data.latitude, data.longitude], 12);
			}
		}

		const layer = L.layerGroup(markers, { id: layerName });
		layer.addTo(map.mapContext.map);
	};

	useMemo(() => {
		if (mapTwo) {
			_addSiteLocation(mapOne.control, siteIdLocation, 'siteIdLocation', true);
			_addSiteLocation(mapTwo.control, siteIdLocation, 'siteIdLocation', false);
		}
	}, [siteIdLocation]);

	const handleChangeMapCompare = (event) => {
		if (event.target.checked) {
			setFormState({
				...formState,
				values: {
					...formState.values,
					[event.target.name]:
						event.target.type === 'checkbox'
							? event.target.checked
							: event.target.value,
					mapSize: 4,
				},
			});
			const currentZoom = mapOne.instance.getZoom();
			setTimeout(() => {
				mapOne.instance.setZoom(currentZoom);
			}, 1);
		} else {
			setFormState({
				...formState,
				values: {
					...formState.values,
					[event.target.name]:
						event.target.type === 'checkbox'
							? event.target.checked
							: event.target.value,
					mapSize: 10,
					mapFilters: true,
				}
			});
		}
	};

	const handleChangeInv = (event) => {
		// if (event.target.checked) {
		// 	setFormState({
		// 		...formState,
		// 		values: {
		// 			...formState.values,
		// 			mapFrameSize: '70vw'
		// 		}
		// 	});
		// } else {
		// 	setFormState({
		// 		...formState,
		// 		values: {
		// 			...formState.values,
		// 			mapFrameSize: '80vw'
		// 		}
		// 	});
		// }

		// mapOne.instance.unsync(mapTwo.instance);
		// mapTwo.instance.unsync(mapOne.instance);
		// mapOne.instance.sync(mapTwo.instance);
		// mapTwo.instance.sync(mapOne.instance);

		mapOne.instance.invalidateSize();
		mapTwo.instance.invalidateSize();
	};

	const handleChangeSync = (event) => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				[event.target.name]:
					event.target.type === 'checkbox'
						? event.target.checked
						: event.target.value
			},
			touched: {
				...formState.touched,
				[event.target.name]: true,
			},
		});

		if (event.target.checked) {
			mapOne.instance.sync(mapTwo.instance);
			mapTwo.instance.sync(mapOne.instance);
			// Fix map drift for Leaflet.sync
			const zooms = mapOne.instance.getZoom();
			mapOne.instance.setZoom(zooms);
		} else {
			mapOne.instance.unsync(mapTwo.instance);
			mapTwo.instance.unsync(mapOne.instance);
		}
	};

	const handleChangeMapFilters = (event) => {
		if (event.target.checked) {
			setFormState({
				...formState,
				values: {
					...formState.values,
					[event.target.name]:
						event.target.type === 'checkbox'
							? event.target.checked
							: event.target.value,
					mapSize: 4
				},
			});
		} else {
			setFormState({
				...formState,
				values: {
					...formState.values,
					[event.target.name]:
						event.target.type === 'checkbox'
							? event.target.checked
							: event.target.value,
					mapSize: 6
				}
			});
		}
	};

	const _autoZoom = (map, data) => {
		const markers = [];

		if (!_.isEmpty(data)) {
			data.forEach(item => {
				const marker = L.circle([item.latitude, item.longitude], {
					color: item.color,
					fillColor: item.color,
					fillOpacity: 0.8,
					radius: 10
				});
				markers.push(marker);
			});

			const layer = L.layerGroup(markers, { id: 'autozoom' });
			layer.addTo(map.mapContext.map);
			const group = new L.featureGroup(markers);
			// fit bounds
			map.mapContext.map.fitBounds(group.getBounds());
		}
		map.RemoveLayerById('autozoom');
	};

	const handleChangeMapAutoZoom = (event) => {
		setFormState({
			...formState,
			values: {
				...formState.values,
				[event.target.name]:
					event.target.type === 'checkbox'
						? event.target.checked
						: event.target.value
			},
			touched: {
				...formState.touched,
				[event.target.name]: true,
			},
		});

		// UE
		if (event.target.checked) {
			if (formState.values.selectedKpiTech === '4g' && formState.values.kpi4g !== undefined) {
				_autoZoom(mapOne.control, driveTest.driveTestPoints);
			} else if (formState.values.selectedKpiTech === '3g' && formState.values.kpi3g !== undefined) {
				_autoZoom(mapOne.control, driveTest.driveTestPoints);
			} else if (formState.values.selectedKpiTech === '2g' && formState.values.kpi2g !== undefined) {
				_autoZoom(mapOne.control, driveTest.driveTestPoints);
			}

			// Failures
			if (formState.values.voiceActive) {
				_autoZoom(mapOne.control, failuresVoice);
			}
			if (formState.values.packetActive) {
				_autoZoom(mapOne.control, failuresPacket);
			}
			if (formState.values.techBandVoiceActive) {
				_autoZoom(mapOne.control, techBandVoice.driveTestTechBand);
			}
			if (formState.values.techBandPacketActive) {
				_autoZoom(mapOne.control, techBandPacket.driveTestTechBand);
			}

			// Map Compare
			// UE
			if (formState.values.selectedKpiTechCompare === '4g' && formState.values.kpi4gCompare !== undefined) {
				_autoZoom(mapTwo.control, driveTestCompare.driveTestPoints);
			} else if (formState.values.selectedKpiTechCompare === '3g' && formState.values.kpi3gCompare !== undefined) {
				_autoZoom(mapTwo.control, driveTestCompare.driveTestPoints);
			} else if (formState.values.selectedKpiTechCompare === '2g' && formState.values.kpi2gCompare !== undefined) {
				_autoZoom(mapTwo.control, driveTestCompare.driveTestPoints);
			}

			// Failures
			if (formState.values.voiceActiveCompare) {
				_autoZoom(mapTwo.control, failuresVoiceCompare);
			}
			if (formState.values.packetActive) {
				_autoZoom(mapTwo.control, failuresPacketCompare);
			}
			if (formState.values.techBandVoiceActiveCompare) {
				_autoZoom(mapTwo.control, techBandVoiceCompare.driveTestTechBand);
			}
			if (formState.values.techBandPacketActiveCompare) {
				_autoZoom(mapTwo.control, techBandPacketCompare.driveTestTechBand);
			}
		}
	};

	const _onSiteIdChange = (value) => {
		if (value === null) {
			mapOne.control.RemoveLayerById('siteIdLocation');
			mapTwo.control.RemoveLayerById('siteIdLocation');
		} else {
			dispatch(GetSiteIdLocation(value.value));
		}
	};

	const _onCarriersColorsClick = (event) => {
		dispatch(ToggleCarriersColorsDialog(true));
	};

	const cardStyle = {
		// height: '10vh'
	};

	const filterOptions = createFilterOptions({
		limit: 20
	});

	const openMapTwo = () => {
		// /
	};

	useEffect(() => {
		openMapTwo();
	}, []);

	return (
		<Card style={cardStyle}>
			<div className={classes.content}>
				<div className={classes.contentSection}>
					<Grid container spacing={1}>
						<Grid item md={12} xs={12}>
							<FormControlLabel
								name="mapCompare"
								control={<Switch color="primary" />}
								label="Traffic Addition Simulation"
								labelPlacement="start"
								checked={formState.values.mapCompare}
								onChange={handleChangeMapCompare}
							/>
							{/* <FormControlLabel
								name="mapSync"
								control={<Switch color="primary" />}
								label="Sync"
								labelPlacement="start"
								checked={formState.values.mapSync}
								onChange={handleChangeSync}
								disabled={!formState.values.mapCompare}
							/> */}
							{/* <FormControlLabel
								name="inv"
								control={<Switch color="primary" />}
								label="inv"
								labelPlacement="inv"
								checked={formState.values.inv}
								onChange={handleChangeInv}
							// disabled={!formState.values.mapCompare}
							/> */}
							{/* <FormControlLabel
								name="mapAutoZoom"
								control={<Switch color="primary" />}
								label="Auto Zoom"
								labelPlacement="start"
								checked={formState.values.mapAutoZoom}
								onChange={handleChangeMapAutoZoom}
							/> */}
							<FormControlLabel
								name="mapFilters"
								control={<Switch color="primary" />}
								label="Filters"
								labelPlacement="start"
								checked={formState.values.mapFilters}
								onChange={handleChangeMapFilters}
								disabled={!formState.values.mapCompare}
							/>
						</Grid>
						{/* <Grid item md={1} xs={1}>
							<FormControlLabel
								name="mapSync"
								control={<Switch color="primary" />}
								label="Sync"
								labelPlacement="start"
								checked={formState.values.mapSync}
								onChange={handleChangeSync}
							/>
						</Grid> */}
						{/* <Grid item md={2} xs={2} align="right">
							<Autocomplete
								id="sitesIds"
								options={sitesIds}
								getOptionLabel={(option) => option.text}
								onChange={(event, value) => { _onSiteIdChange(value); }}
								renderInput={(params) => <TextField {...params} label="SiteId" variant="outlined" size="small" />}
								filterOptions={filterOptions}
							/>
						</Grid> */}
						{/* <Grid item md={3} xs={3} align="right">
							<div className={classes.button}>
								<Button
									className={classes.agreeButton}
									color="primary"
									onClick={_onCarriersColorsClick}
									variant="contained"
									size="small"
								>
									Carriers Colors
								</Button>
							</div>
						</Grid> */}
					</Grid>
				</div>
			</div>
		</Card>
	);
};

CompareBox.propTypes = {
	className: PropTypes.string,
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	onFilter: PropTypes.func.isRequired,
	filterDisabled: PropTypes.bool,
	onClear: PropTypes.func
};

export default CompareBox;
